import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {  
  Stack,
  PrimaryButton,
  DefaultButton,
  IconButton,
  Text,
  TextField
  } from "@fluentui/react";
import { DismissRegular, Search20Regular } from "@fluentui/react-icons";
import {
  SpeechRecognizer,
  ResultReason,
} from "microsoft-cognitiveservices-speech-sdk";
import { v4 as uuidv4 } from "uuid";

import styles from "./Chat.module.css";
import { multiLingualSpeechRecognizer } from "../../util/SpeechToText";
import { useBoolean } from "@fluentui/react-hooks";
import {
  ChatMessage,
  ConversationRequest,
  callConversationApiStreamed,
  Citation,
  ChatResponse,
  historyList,
  Conversation,
  historyDeleteAll,
  historyReadMessages,
  getFrontEndSettings,
  Feedback,
  OpenAiModel,
  SearchIndex,
  ConversationRating,
  historyGiveConversationFeedback,
  requestResolutionCode,
  ConversationAttachment,
  uploadConversationFile,
  deleteConversationFile,
  getConversationAttachments
} from "../../api";

import { getPreferredAiModel, getPreferredAiTemperature, getPreferredResponseMode, getShowedStartupScreen, getPreferredSearchIndex } from "../../components/Utils/utils";
import { useShell } from "../../shell/context/ShellContext";
import { getResumePoint, setResumePoint, clearResumePoint, pruneExpiredResumePoints } from "../../util/resumeStorage";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import Layout from "../layout/Layout";
import { AssistantTypeSection } from "../../components/AssistantTypeSection/AssistantTypeSection";
import { StartupScreen } from "../../components/StartupScreen/StartupScreen";
import { ChatMessageContainer } from "../../components/ChatMessageContainer/ChatMessageContainer";
import { CitationPanel } from "../../components/CitationPanel/CitationPanel";
import { SettingsPanel } from "../../components/SettingsPanel/SettingsPanel";
import { usePromptTemplates } from "../../context/PromptTemplateContext";
import { stripTemplateContent } from "../../util/promptTemplateUtils";
import { useErrorNotification } from "../../context/ErrorNotificationContext";
import { ExportModal } from "../../components/ExportModal";
import { ChatFindBar } from "../../components/ChatFindBar/ChatFindBar";


const OFFSET_INCREMENT = 25;
const [ASSISTANT] = ["assistant", "tool", "error"];

const toFriendlyErrorMessage = (raw: string): string => {
  const msg = raw.toLowerCase();
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network request failed") || msg.includes("load failed")) {
    return "We couldn't reach the server. Please check your internet connection and try again.";
  }
  if (msg.includes("401") || msg.includes("unauthorized")) {
    return "Your session has expired. Please refresh the page and sign in again.";
  }
  if (msg.includes("403") || msg.includes("forbidden")) {
    return "You don't have permission to perform this action.";
  }
  if (msg.includes("404") || msg.includes("not found")) {
    return "The requested resource could not be found. Please try again or start a new conversation.";
  }
  if (msg.includes("429") || msg.includes("too many requests") || msg.includes("rate limit")) {
    return "You're sending messages too quickly. Please wait a moment and try again.";
  }
  if (msg.includes("500") || msg.includes("internal server")) {
    return "Something went wrong on our end. Please try again in a moment.";
  }
  if (msg.includes("503") || msg.includes("service unavailable") || msg.includes("unavailable")) {
    return "The service is temporarily unavailable. Please try again shortly.";
  }
  if (msg.includes("timeout") || msg.includes("timed out") || msg.includes("aborted")) {
    return "The request took too long to complete. Please try again.";
  }
  if (!raw || raw.trim() === "") {
    return "An unexpected error occurred. Please try again. If the problem persists, contact support.";
  }
  return "An unexpected error occurred. Please try again. If the problem persists, contact support.";
};

const Chat = () => {
  const { showError } = useErrorNotification();
  const { templates, loading: templatesLoading } = usePromptTemplates();
  const { settingsPanelOpen: isSettingsPanelOpen, setSettingsPanelOpen: setIsSettingsPanelOpen, setCurrentConversationId, setOnRateConversation, setOnNewChat, pendingRateConvId, setPendingRateConvId, chatHistory: shellChatHistory, setChatHistory: setShellChatHistory, isAssistantAPILoading, availableModels: shellAvailableModels, availableIndexes: shellAvailableIndexes } = useShell();
  const [searchParams] = useSearchParams();
  const lastQuestionRef = useRef<string>("");
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const dragCounterRef = useRef<number>(0);
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const loadingTopRef = useRef<HTMLDivElement | null>(null);
  const [_isLoading, _setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);  // Add this state
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const isRetryRef = useRef(false);
  // Synchronous guard against duplicate concurrent requests.
  // React state (isGenerating) requires a re-render to take effect, leaving a
  // brief window where a second click can fire before the button is disabled.
  // A ref update is immediate and prevents the race entirely.
  const isGeneratingRef = useRef(false);
  
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [isSendButtonDisabled, setSendButtonDisabled] = useState(false);
  const [activeCitation, setActiveCitation] = useState<Citation>();  
  const [conversationTitle, setConversationTitle] = useState<string>("");
  const [answers, setAnswers] = useState<ChatMessage[]>([]);
  const [showConversationFeedbackPopup, setShowConverationFeedbackPopup] = React.useState(false);
  const abortFuncs = useRef([] as AbortController[]);
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [userMessage, setUserMessage] = useState("");
  const [externalInputValue, setExternalInputValue] = useState<string>("");
  const [recognizedText, setRecognizedText] = useState<string>("");
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [isTextToSpeechActive, setIsTextToSpeechActive] = useState(false);


  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);

  const [fetchingChatHistory, setFetchingChatHistory] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [chatHistory, setChatHistoryLocal] = useState<Conversation[]>([]);
  const [hasMoreRecords, setHasMoreRecords] = useState<boolean>(true);
  const [selectedConvId, setSelectedConvId] = useState<string>("");

  // Live refs: always hold the current value of key state so callbacks registered
  // once on mount (stale closures) can still read the latest values.
  const answersRef = useRef<ChatMessage[]>([]);
  answersRef.current = answers;
  const selectedConvIdRef = useRef<string>("");
  selectedConvIdRef.current = selectedConvId;
  const conversationIdRef = useRef<string>(conversationId);
  conversationIdRef.current = conversationId;
  const [_hideClearAllDialog, { toggle: toggleClearAllDialog }] = useBoolean(true);
  const [_clearing, setClearing] = React.useState(false);
  const [_clearingError, setClearingError] = React.useState(false);
  const [fetchingConvMessages, setFetchingConvMessages] = React.useState(false);
  const [isInitialAPItriggered, setIsInitialAPItriggered] = useState(false);

  const [availableModels, setAvailableModels] = useState<OpenAiModel[]>(shellAvailableModels);
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>(shellAvailableIndexes);

  const [_currentConversation, setCurrentConversation] = useState<Conversation | undefined>();
  const [conversationRating, setConversationRating] = useState<ConversationRating | undefined>();
  const [conversationFeedbackComment, setConversationFeedbackComment] = useState<string>("");
  const [showStartupScreen, setShowStartupScreen] = useState<boolean>(false);

  const [conversationAttachments, setConversationAttachments] = useState<ConversationAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<{ message: string; details?: string } | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [_showStartNewChatBtn, setShowStartNewChatBtn] = useState<boolean>(false);
  // Tracks how many messages were loaded from history when a previous conversation is resumed.
  // Used to render a "continuing session" divider in the chat view.
  const [historyMessageCount, setHistoryMessageCount] = useState<number | undefined>(undefined);
  const sessionStartTimeRef = useRef<Date | null>(null);
  // Stable ID unique to this browser tab — prevents cross-tab key collisions in sessionStorage
  const tabIdRef = useRef<string>(uuidv4());
  const broadcastRef = useRef<BroadcastChannel | null>(null);

  // ── Find in chat state ──────────────────────────────────────────────────────
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [findCurrentIndex, setFindCurrentIndex] = useState(0);
  const [findTotal, setFindTotal] = useState(0);
  const chatStreamRef = useRef<HTMLDivElement>(null);

  // Wrapper: updates both the local chat history and the shell sidebar in sync.
  // The shell update is applied to ShellContext's own previous state (not derived
  // from local state) to avoid wiping sidebar history when local state is empty
  // on initial mount (ShellContext loads history independently via refreshChatHistory).
  const setChatHistory = useCallback((value: React.SetStateAction<Conversation[]>) => {
    setChatHistoryLocal((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return next;
    });
    // Apply the same update to shell state using shell's own previous state.
    queueMicrotask(() => setShellChatHistory((shellPrev) =>
      typeof value === "function" ? value(shellPrev) : value
    ));
  }, [setShellChatHistory]);

  // Populate local chatHistory from ShellContext once it loads on mount.
  // ShellContext.refreshChatHistory() populates shellChatHistory asynchronously;
  // local chatHistory is needed for message caching (getMessagesByConvId).
  useEffect(() => {
    if (shellChatHistory.length > 0 && chatHistory.length === 0) {
      setChatHistoryLocal(shellChatHistory);
    }
  }, [shellChatHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync models/indexes from ShellContext when they arrive after mount.
  useEffect(() => {
    if (shellAvailableModels.length > 0) setAvailableModels(shellAvailableModels);
  }, [shellAvailableModels]);
  useEffect(() => {
    if (shellAvailableIndexes.length > 0) setAvailableIndexes(shellAvailableIndexes);
  }, [shellAvailableIndexes]);

  // Re-strip template content from answers when templates finish loading OR when answers change.
  // Guards against two race conditions:
  //   1. Conversation history arrives BEFORE templates are loaded — templates were empty when
  //      stripTemplateContent ran inside onSelectConversation, so raw content was stored.
  //      When templates finish loading this effect fires and cleans up.
  //   2. Templates are already loaded and THEN answers arrive (e.g. fetch completes after
  //      templates loaded but before this effect last ran with answers.length > 0) — without
  //      answers in the dep array the effect would never re-run for this case.
  //
  // stripTemplateContent returns the *same* array reference when nothing changed, making
  // the effect idempotent: the second run (after setAnswers(stripped)) sees stripped===answers
  // and exits early, preventing an infinite loop.  The cache is also kept in sync.
  useEffect(() => {
    if (templatesLoading || templates.length === 0 || answers.length === 0) return;
    const stripped = stripTemplateContent(answers, templates);
    if (stripped === answers) return; // nothing changed — skip state update
    setAnswers(stripped);
    if (selectedConvId) setMessagesByConvId(selectedConvId, stripped);
  }, [templates, templatesLoading, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle startup screen completion
  const handleStartupComplete = () => {
    setShowStartupScreen(false);
  };

  // Extract proposed answers from the latest assistant message
  const proposedAnswers = useMemo(() => {
    // Find the most recent assistant message that has proposed answers
    const assistantMessages = answers.filter(msg => msg.role === ASSISTANT);
    const latestAssistantMessage = assistantMessages[assistantMessages.length - 1];

    return latestAssistantMessage?.proposedAnswers || [];
  }, [answers]);

  const canRequestResolutionCode = useMemo(() => {
    // Find the most recent assistant message that has proposed answers
    const assistantMessages = answers.filter(msg => msg.role === ASSISTANT);
    const latestAssistantMessage = assistantMessages[assistantMessages.length - 1];

    //console.log(latestAssistantMessage);

    return latestAssistantMessage?.canBeResolved || false;
  }, [answers]);

  // Show a retry button when the last message in the conversation is an unanswered user message
  // (e.g. the page was refreshed mid-stream or streaming broke before the reply arrived).
  const canRetry = !isGenerating && answers.length > 0 && answers[answers.length - 1]?.role === "user";

  const handleRetry = () => {
    // Double-guard: check both the ref (synchronous) and state (rendered).
    if (isGeneratingRef.current || isGenerating) return;
    const lastMsg = answers[answers.length - 1];
    if (!lastMsg || lastMsg.role !== "user") return;
    makeStreamedApiRequest(lastMsg.content, false, true);
  };

  // ── Find in chat: count matches and manage active highlight ─────────────────
  useEffect(() => {
    if (!chatStreamRef.current || !findQuery.trim()) {
      setFindTotal(0);
      return;
    }
    // Run after paint so the highlighted <mark> elements are in the DOM
    const frame = requestAnimationFrame(() => {
      const matches = chatStreamRef.current?.querySelectorAll("[data-find-match]");
      const count = matches?.length ?? 0;
      setFindTotal(count);
      // Clamp currentIndex if matches decreased
      setFindCurrentIndex((prev) => (count === 0 ? 0 : Math.min(prev, count - 1)));
    });
    return () => cancelAnimationFrame(frame);
  }, [findQuery, answers]);

  useEffect(() => {
    if (!chatStreamRef.current || !findQuery.trim() || findTotal === 0) return;
    const matches = chatStreamRef.current.querySelectorAll<HTMLElement>("[data-find-match]");
    matches.forEach((el, i) => {
      if (i === findCurrentIndex) {
        el.setAttribute("data-find-active", "true");
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        el.removeAttribute("data-find-active");
      }
    });
  }, [findCurrentIndex, findTotal, findQuery]);

  // ── Global Ctrl+F / Cmd+F to open find bar ───────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        // Only intercept when chat messages are visible
        if (answers.length > 0) {
          e.preventDefault();
          setFindOpen(true);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [answers.length]);

  const handleFindClose = () => {
    setFindOpen(false);
    setFindQuery("");
    setFindCurrentIndex(0);
    setFindTotal(0);
    // Clear all active markers left in DOM
    chatStreamRef.current?.querySelectorAll<HTMLElement>("[data-find-active]").forEach((el) => {
      el.removeAttribute("data-find-active");
    });
  };

  const handleFindNext = () => {
    if (findTotal === 0) return;
    setFindCurrentIndex((prev) => (prev + 1) % findTotal);
  };

  const handleFindPrev = () => {
    if (findTotal === 0) return;
    setFindCurrentIndex((prev) => (prev - 1 + findTotal) % findTotal);
  };

  // Handle feedback updates
  const handleFeedbackUpdate = (messageId: string, feedback: Feedback) => {
    setAnswers(prevAnswers =>
      prevAnswers.map(answer =>
        answer.id === messageId
          ? { ...answer, feedback }
          : answer
      )
    );

    // Also update the chat history if this conversation is selected
    if (selectedConvId) {
      const updatedMessages = answers.map(answer =>
        answer.id === messageId
          ? { ...answer, feedback }
          : answer
      );
      setMessagesByConvId(selectedConvId, updatedMessages);
    }
  };

  // Streamed API request using SSE
  const handleFileSelected = async (files: FileList) => {
    const currentConvId = selectedConvId || conversationId;
    setUploadingFiles(true);
    setUploadError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = await uploadConversationFile(currentConvId, file);
        setConversationAttachments(prev => [...prev, result]);
        if (result.was_summarized) {
          console.warn(`Document "${file.name}" was large and has been summarized — responses may have reduced accuracy.`);
        }
        // Check if the uploaded file is an image and the current model lacks vision support
        if (result.mime_type.startsWith("image/")) {
          const preferredModel = getPreferredAiModel();
          const activeModel = availableModels.find(m => m.deploymentName === preferredModel) ?? availableModels[0];
          if (activeModel && !activeModel.supportsVision) {
            setUploadWarning(`Image understanding with ${activeModel.displayName} is limited to OCR text extraction. Switch to a vision-capable model for better results.`);
          }
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const message = error instanceof Error ? error.message : "Upload failed.";
      const details = error instanceof Error ? (error as unknown as { details?: string }).details : undefined;
      if (message.toLowerCase().includes("not allowed")) {
        setUploadError({ message: `File type not allowed. Supported: PDF, DOCX, TXT, MD, CSV, JSON, XML, LOG, PNG, JPG, WEBP, SVG.` });
      } else if (message.toLowerCase().includes("size")) {
        setUploadError({ message: "File exceeds the maximum allowed size (20 MB). Please upload a smaller file." });
      } else if (details) {
        setUploadError({ message, details });
      } else {
        setUploadError({ message: "Upload failed. Please check your network connection and try again." });
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleChatDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const handleChatDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleChatDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleChatDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelected(files);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await deleteConversationFile(attachmentId);
      setConversationAttachments(prev => {
        const remaining = prev.filter(a => a.id !== attachmentId);
        if (!remaining.some(a => a.mime_type.startsWith("image/"))) {
          setUploadWarning(null);
        }
        return remaining;
      });
    } catch (error) {
      console.error("Error deleting attachment:", error);
    }
  };

  const makeStreamedApiRequest = async (question: string, isProposedAnswer: boolean, isRetry = false) => {

    // Synchronous guard: if a request is already in flight, ignore this call.
    // This prevents duplicate responses when the user clicks Retry and
    // simultaneously presses Enter (or clicks Send) before React has had a
    // chance to re-render and disable the send button via isGenerating state.
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    isRetryRef.current = isRetry;
    lastQuestionRef.current = question;

    // Capture before any state changes — used to distinguish new vs existing conversations.
    const wasNewConversation = !selectedConvId && !isRetry;
    const effectiveConvId = selectedConvId || conversationId;

    const ATTACHMENT_STRONG_RE = /\b(attachment|pdf|docx|spreadsheet)\b/i;
   
    // Match only when an intent verb directly precedes a file reference — prevents false positives
    // from data content like "the checkout must lock the document for B3 processing"
    const ATTACHMENT_INTENT_RE = /\b(?:analyze|analyse|describe|explain|read|summarize|summarise|check|review|examine|interpret|open|parse|extract|look\s+at|upload)\s+(?:this|the|my|uploaded|attached|above|following)\s+(?:image|picture|photo|screenshot|file|document|csv|report)\b/i;
    const ATTACHMENT_EXPLICIT_RE = /\b(?:uploaded|attached)\s+(?:image|picture|photo|screenshot|file|document|csv|report)\b/i;
    const ATTACHMENT_REQUEST_RE = { test: (q: string) => ATTACHMENT_STRONG_RE.test(q) || ATTACHMENT_INTENT_RE.test(q) || ATTACHMENT_EXPLICIT_RE.test(q) };
    const VISUAL_DESCRIBE_RE = /\b(describe|analyze|analyse|explain|interpret|summarize|summarise|what'?s?\s+(?:in|on|shown|this)|tell\s+me\s+about|read\s+(?:the|this))\b[\s\S]{0,60}\b(diagram|figure|chart|graph|drawing|visual)\b/i;

    const hasAnyAttachment = conversationAttachments.length > 0;

    // --- Intent-aware file validation ---
    // Step 1: Determine if the prompt is a slash command mapped to a known template.
    // Step 2: Choose what text to validate:
    //   - For templates: only the user input after the command (e.g. "analyze the uploaded file")
    //     This avoids false positives from keywords inside long pasted data payloads.
    //   - For regular messages: the full question text.
    // Step 3: Only enforce file validation when the prompt genuinely requires a file.
    const slashCmdMatch = question.match(/^\/([a-z0-9-]+)/i);
    const matchedTemplate = slashCmdMatch
      ? templates.find(t => t.promptKey === slashCmdMatch[1])
      : null;

    // For slash commands, validate only the user's intent (text after the command name).
    // For regular messages, validate the full question.
    const textToValidate = matchedTemplate
      ? question.replace(/^\/[a-z0-9-]+\s*/i, '').trim()
      : question;

    const promptRequiresFile =
      ATTACHMENT_REQUEST_RE.test(textToValidate) || VISUAL_DESCRIBE_RE.test(textToValidate);

    if (promptRequiresFile && !hasAnyAttachment && !isProposedAnswer) {
      const userMsg: ChatMessage = {
        role: "user",
        content: question,
        id: uuidv4(),
        date: new Date().toISOString(),
        endTurn: true,
      };
      const noAttachmentMsg: ChatMessage = {
        role: "assistant",
        content: "I couldn't find any uploaded file or attachment. Please upload a file so I can help with this request.",
        id: uuidv4(),
        date: new Date().toISOString(),
        endTurn: true,
      };
      setAnswers(prev => [...prev, userMsg, noAttachmentMsg]);
      setShowStartNewChatBtn(true);
      return;
    }

    // For brand-new conversations, immediately insert a draft entry into the chat history so
    // the sidebar shows it right away.  The draft is refined with the real title once streaming
    // completes.  This also means a page-refresh after the backend's early-persist step will
    // show the conversation in the list rather than losing it.
    if (wasNewConversation) {
      const draftConversation: Conversation = {
        id: effectiveConvId,
        title: question.slice(0, 50),
        messages: [],
        date: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setChatHistory(prev => [draftConversation, ...prev]);
      setSelectedConvId(effectiveConvId);
    }

    setIsGenerating(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    // ── Server-side template resolution ───────────────────────────────────────────────────
    // When the user invokes a slash command (e.g. "/prd some text"), extract the promptKey
    // and send it to the backend via `prompt_key`.  The backend resolves the active template
    // version from the database and injects it as a system prompt — it is NEVER embedded in
    // the persisted user message.
    //
    // IMPORTANT: Do NOT expand the template content here on the client.  Doing so embeds the
    // template body into the message that the backend stores, which causes leakage into chat
    // history when the template is subsequently versioned (the old embedded content no longer
    // matches the current version and stripTemplateContent cannot clean it up).
    let messageToSend = question;
    let resolvedPromptKey: string | undefined;

    const slashMatch = question.match(/^\/([a-z0-9-]+)/);
    if (slashMatch) {
      const promptKey = slashMatch[1];
      const template = templates.find(t => t.promptKey === promptKey);

      if (template) {
        // Send only the user's text after the slash command; pass the key separately.
        const userInput = question.replace(/^\/[a-z0-9-]+\s*/, '').trim();
        messageToSend = userInput || question; // fall back to full question if no text after key
        resolvedPromptKey = promptKey;
      }
    }
    // ── End server-side template resolution ───────────────────────────────────────────────

    // Inject current system datetime when the question is time/date-related so the
    // AI model always has accurate clock information from the client side.
    const timeRelatedPattern = /\b(time|clock|hour|minute|second|date|today|now|when)\b/i;
    if (timeRelatedPattern.test(question)) {
      const now = new Date();
      const readableDateTime = now.toLocaleString(undefined, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'
      });
      messageToSend = `[System datetime: ${readableDateTime}]\n\n${messageToSend}`;
    }

    const userMessage: ChatMessage = {
      role: "user",
      content: question,
      id: uuidv4(),
      date: new Date().toISOString(),
      endTurn: true,
      attachments: conversationAttachments.length > 0 ? [...conversationAttachments] : undefined,
    };

    let preferredAiModel = getPreferredAiModel() ?? undefined;

    const temperature = getPreferredAiTemperature(preferredAiModel) ?? undefined;
    const responseMode = getPreferredResponseMode() ?? undefined;
        
    // If no explicit model preference, map response mode to its default model
    if (preferredAiModel === undefined) {
      if (responseMode === "simple") {
        preferredAiModel = "gpt-5-nano";
      } else if (responseMode === "standard") {
        preferredAiModel = "gpt-5-mini";
      } else if (responseMode === "enhanced") {
        preferredAiModel = "gpt-4.1";
      }
    }

    const request: ConversationRequest = {
      conversation_id: effectiveConvId,
      message: messageToSend,
      is_proposed_answer: isProposedAnswer,
      requested_model: preferredAiModel,
      temperature: temperature,
      response_mode: responseMode,
      preferred_index_id: getPreferredSearchIndex() ?? undefined,
      is_retry: isRetry || undefined,
      prompt_key: resolvedPromptKey,
    };

    console.log(request);

    let latestResponse: ChatResponse = {     
      date: new Date().toISOString()      
    };

    let currentAnswer: ChatMessage | null = null;
    let _answerAppended = false;
    let _answerCompleted = false;
    let requestMessages = answersRef.current;
    // True once actual AI content (not backend status messages) starts streaming.
    // Backend status prefixes: ⊙ Searching, ⊞ Retrieved, ✦ Composing
    let realContentStarted = false;

    const updateRequestMessages = (updater: (messages: ChatMessage[]) => ChatMessage[]) => {
      setAnswers(prev => {
        const next = updater(prev);
        requestMessages = next;
        return next;
      });
    };

    const buildCompletedMessages = (messages: ChatMessage[]) => {
      if (!currentAnswer) return messages;
      const completedAnswer = { ...currentAnswer };

      if (isRetry) {
        const lastUserIndex = messages.reduceRight(
          (found, msg, index) => found === -1 && msg.role === "user" ? index : found,
          -1
        );
        const retryBase = lastUserIndex >= 0 ? messages.slice(0, lastUserIndex + 1) : messages;
        return [...retryBase, completedAnswer];
      }

      const requestUserIndex = messages.findIndex(msg => msg.id === userMessage.id);
      if (requestUserIndex >= 0) {
        return [...messages.slice(0, requestUserIndex + 1), completedAnswer];
      }

      return [...messages, userMessage, completedAnswer];
    };

    try {
      await callConversationApiStreamed(
        request,
        abortController.signal,
        (response: ChatResponse) => {
          // Guard: if the request was aborted (e.g. user clicked New Chat), ignore
          // any callbacks that fire after the abort — prevents stale data from
          // appearing in the freshly-cleared new chat.
          if (abortController.signal.aborted) return;

          latestResponse = response;

          //console.log(latestResponse);

          setShowLoadingMessage(false);

          if (response.error) {
            // Error from backend — clear status and record as real content
            // so the cleanup in finally does not attempt to remove it.
            setStreamingStatus(null);
            realContentStarted = true;
            currentAnswer = {
              role: "error",
              content: response.error,
              id: uuidv4(),
              date: new Date().toISOString(),
              endTurn: true
            };
          } else if (response.message == null) {
            setStreamingStatus(null);
            realContentStarted = true;
            currentAnswer = {
              role: "error",
              content: "Unable to parse the response",
              id: uuidv4(),
              date: new Date().toISOString(),
              endTurn: true
            };
          } else {

            const message = response.message;

            if (message.isStatusMessage) {
              // Transient backend status update (fetching / retrieving / thinking).
              // Display in the loading area only — never add to the answers list so
              // that stopping at this stage leaves the chat exactly as it was.
              setStreamingStatus(message.content);
              return;
            }

            // Real AI content is now streaming.
            if (!realContentStarted) {
              realContentStarted = true;
              setStreamingStatus(null);
            }

            if (!currentAnswer) {
              currentAnswer = { ...message, model: preferredAiModel };
              _answerAppended = true;
              // On retry the user message is already in answers — only append the assistant reply.
              if (isRetry) {
                updateRequestMessages(prev => [...prev, currentAnswer!]);
              } else {
                updateRequestMessages(prev => [...prev, userMessage, currentAnswer!]);
              }
            } else {
              currentAnswer.id = message.id;
              currentAnswer.content = message.content;
              currentAnswer.endTurn = message.endTurn;
              if (message.endTurn) _answerCompleted = true;
              currentAnswer.canBeResolved = message.canBeResolved;
              currentAnswer.citations = message.citations;
              currentAnswer.proposedAnswers = message.proposedAnswers;
              updateRequestMessages(prev => {
                const newAnswers = [...prev];
                const lastIndex = newAnswers.length - 1;
                if (newAnswers[lastIndex] && newAnswers[lastIndex].role === "assistant") {
                  newAnswers[lastIndex] = { ...currentAnswer! };
                }
                return newAnswers;
              });
            }
          }
        }
      );

      // Guard: if clearChat was called (abort signaled) while we were awaiting the stream,
      // skip ALL state mutations here. clearChat has already committed its own state
      // updates (blank new chat), and we must not override them.
      if (!abortController.signal.aborted) {
        const updatedMessages = buildCompletedMessages(requestMessages);
        console.log(latestResponse);
        setAnswers(updatedMessages);

        // Use the pre-captured flags rather than reading selectedConvId from state (which may
        // have been updated by the draft-insertion at the top of this function).
        const finalConvId = latestResponse?.conversationId ?? effectiveConvId;

        if (wasNewConversation && finalConvId) {
          const finalTitle = latestResponse?.title ?? question.slice(0, 50);
          const newConversation: Conversation = {
            id: finalConvId,
            title: finalTitle,
            messages: updatedMessages,
            date: latestResponse?.date ?? new Date().toISOString(),
            updatedAt: latestResponse?.date ?? new Date().toISOString(),
          };

          setConversationTitle(finalTitle);

          // Replace the draft entry with the finalised conversation data.
          setChatHistory((prevHistory) => {
            const idx = prevHistory.findIndex(c => c.id === finalConvId);
            if (idx !== -1) {
              const updated = [...prevHistory];
              updated[idx] = newConversation;
              if (idx > 0) updated.unshift(updated.splice(idx, 1)[0]);
              return updated;
            }
            return [newConversation, ...prevHistory];
          });
          setSelectedConvId(finalConvId);
          setCurrentConversation(newConversation);
          broadcastRef.current?.postMessage({ type: "CONVERSATION_UPDATED", convId: finalConvId });
        } else {
          // Update existing conversation with new messages, title (if AI generated one), and updatedAt.
          setMessagesByConvId(effectiveConvId, updatedMessages);

          // If the backend generated a title (e.g. after a retry on a draft conversation),
          // update the chat header bar too.
          if (latestResponse?.title) {
            setConversationTitle(latestResponse.title);
          }

          setChatHistory((prevHistory) => {
            const updatedHistory = [...prevHistory];
            const conversationIndex = updatedHistory.findIndex(conv => conv.id === effectiveConvId);

            if (conversationIndex > -1) {
              updatedHistory[conversationIndex] = {
                ...updatedHistory[conversationIndex],
                updatedAt: latestResponse!.date,
                // Carry through AI-generated title if present (e.g. first reply in a retried chat)
                ...(latestResponse?.title ? { title: latestResponse.title } : {}),
              };

              if (conversationIndex > 0) {
                const updatedConversation = updatedHistory.splice(conversationIndex, 1)[0];
                updatedHistory.unshift(updatedConversation);
              }
            }

            return updatedHistory;
          });
          broadcastRef.current?.postMessage({ type: "CONVERSATION_UPDATED", convId: effectiveConvId });
        }
      }

    } catch (e) {
      if (!abortController.signal.aborted) {
        const raw = e instanceof Error ? e.message : "";
        const friendly = toFriendlyErrorMessage(raw);
        showError(friendly);
      }
    } finally {
      isGeneratingRef.current = false;
      setIsGenerating(false);
      setShowLoadingMessage(false);
      isRetryRef.current = false;
      // Always clear any lingering status text so it never persists after the
      // request ends (whether completed, aborted, or errored).
      setStreamingStatus(null);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
      // If the request was aborted before real content arrived, nothing was
      // added to `answers` (status messages are tracked separately), so there
      // is nothing to remove — the chat is already in a clean state.
    }
  };

  // Buffer to store recognized text
  let recognizedTextBuffer = "";
  let currentSentence = "";

  const startSpeechRecognition = async () => {
    if (!isRecognizing) {
      setIsRecognizing(true);
      recognizerRef.current = await multiLingualSpeechRecognizer(); // Store the recognizer in the ref

      recognizerRef.current.recognized = (s, e) => {
        if (e.result.reason === ResultReason.RecognizedSpeech) {
          const recognizedText = e.result.text.trim();
          // Append current sentence to buffer if it's not empty
          if (currentSentence) {
            recognizedTextBuffer += ` ${currentSentence.trim()}`;
            currentSentence = "";
          }
          // Start new sentence
          currentSentence += ` ${recognizedText}`;
          //set text in textarea
          setUserMessage((recognizedTextBuffer + currentSentence).trim());
          setRecognizedText((recognizedTextBuffer + currentSentence).trim());
        }
      };

      recognizerRef.current.startContinuousRecognitionAsync(
        () => {
          setIsRecognizing(true);
          setIsListening(true);
        },
        (error) => {
          console.error(`Error starting recognition: ${error}`);
        }
      );
    }
  };

  const stopSpeechRecognition = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecognizing) {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
        });
      }
      setIsRecognizing(false);
      setRecognizedText("");
      setSendButtonDisabled(false);
      setIsListening(false);
    }
  };

  const onMicrophoneClick = async (
    e: React.KeyboardEvent | React.MouseEvent
  ) => {
    // clear the previous text
    e.preventDefault();
    e.stopPropagation();
    setUserMessage("");
    setRecognizedText("");
    if (!isRecognizing) {
      setSendButtonDisabled(true);
      await startSpeechRecognition();
    } else {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
        });
      }
      setIsRecognizing(false);
      setSendButtonDisabled(false);
      setIsListening(false);
      setRecognizedText(userMessage);
    }
  };

  const onRequestResolutionCode = async () => {
    if (!selectedConvId && !conversationId) {
      console.error("No conversation ID available");
      return;
    }

    const convId = selectedConvId || conversationId;

    try {
      setIsGenerating(true);
      setShowLoadingMessage(true);
      lastQuestionRef.current = "Give me the resolution code";

      // Create a user message to represent the resolution code request
      const userMessage: ChatMessage = {
        role: "user",
        content: "Give me the resolution code",
        id: uuidv4(),
        date: new Date().toISOString(),
        endTurn: true
      };

      // Request the resolution code from the backend
      const response = await requestResolutionCode(convId);

      if (response.error) {
        // Handle error response
        const errorMessage: ChatMessage = {
          role: "error",
          content: response.error,
          id: uuidv4(),
          date: new Date().toISOString(),
          endTurn: true
        };
        setAnswers(prev => [...prev, errorMessage]);
      } else if (response.message) {
        // Add the assistant's response message
        setAnswers(prev => [...prev, userMessage, response.message!]);

        // Update conversation history similar to makeStreamedApiRequest
        const updatedMessages = [...answers, userMessage, response.message];

        // Update existing conversation
        setMessagesByConvId(convId, updatedMessages);

        // Update the conversation's updatedAt timestamp and move it to the front
        setChatHistory((prevHistory) => {
          const updatedHistory = [...prevHistory];
          const conversationIndex = updatedHistory.findIndex(conv => conv.id === convId);

          if (conversationIndex > -1) {
            updatedHistory[conversationIndex] = {
              ...updatedHistory[conversationIndex],
              updatedAt: response.date
            };

            if (conversationIndex > 0) {
              const updatedConversation = updatedHistory.splice(conversationIndex, 1)[0];
              updatedHistory.unshift(updatedConversation);
            }
          }

          return updatedHistory;
        });
        
      }

      // Scroll to the new message
      scrollToBottom("smooth");
    } catch (error) {
      console.error("Failed to request resolution code:", error);
      const raw = error instanceof Error ? error.message : "";
      showError(toFriendlyErrorMessage(raw) || "Failed to request resolution code. Please try again.");
    } finally {
      setIsGenerating(false);
      setShowLoadingMessage(false);
    }
  };

  const clearChat = () => {
    // If a request is in-flight, abort it and preserve the pending question so
    // the retry button is visible when the user navigates back to that conversation.
    if (isGeneratingRef.current || isGenerating) {
      const pendingQuestion = lastQuestionRef.current;

      // Read LIVE values from refs — not from the closure which may be stale because
      // clearChat is registered as onNewChat only once on mount (stale closure problem).
      const currentConvId = selectedConvIdRef.current || conversationIdRef.current;
      const currentAnswers = answersRef.current;

      // Abort all in-flight requests BEFORE touching state.
      // The guards on the streaming callback and post-await code check
      // abortController.signal.aborted and skip state mutations when true.
      abortFuncs.current.forEach((a) => a.abort());
      abortFuncs.current = [];

      // Persist the pending question into the conversation's history cache so that
      // when the user later clicks the conversation in the sidebar the question is
      // visible and the retry button is active.
      if (pendingQuestion && currentConvId) {
        // Determine which phase the request was aborted in:
        //
        // Phase 1 — status-message phase (⊙ Searching / ⊞ Retrieved / ✦ Composing):
        //   The user message has NOT been added to answers yet.
        //   Detected when there is no user message with matching content in currentAnswers.
        //
        // Phase 2 — real-content streaming phase:
        //   The user message IS already in answers, followed by a partial assistant
        //   message.  Strip the partial assistant so the user message is last, making
        //   canRetry = true when the conversation is revisited.
        const lastUserMsgIdx = currentAnswers.reduceRight(
          (found, msg, i) => (found === -1 && msg.role === "user" ? i : found),
          -1
        );
        const isPhase2 =
          lastUserMsgIdx >= 0 &&
          currentAnswers[lastUserMsgIdx].content === pendingQuestion;

        let messagesToSave: ChatMessage[];
        if (isPhase2) {
          // Strip the partial assistant message; leave user message as the last entry.
          messagesToSave = currentAnswers.slice(0, lastUserMsgIdx + 1);
        } else {
          // User message not yet in answers — append it so canRetry becomes true.
          const pendingUserMsg: ChatMessage = {
            role: "user",
            content: pendingQuestion,
            id: uuidv4(),
            date: new Date().toISOString(),
            endTurn: true,
          };
          messagesToSave = [...currentAnswers, pendingUserMsg];
        }

        // Update chatHistory for the old conv so the sidebar shows it and retry works.
        // If the draft hasn't been committed yet (race during Phase 1), create it here.
        setChatHistory((prev) => {
          const existingIdx = prev.findIndex(
            (c) => String(c.id) === String(currentConvId)
          );
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = {
              ...updated[existingIdx],
              messages: messagesToSave,
            };
            return updated;
          }
          // Draft not yet in chatHistory — create it now.
          const draftConv: Conversation = {
            id: currentConvId,
            title: pendingQuestion.slice(0, 50),
            messages: messagesToSave,
            date: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          return [draftConv, ...prev];
        });
      }

      // Reset generating flags so the finally block in makeStreamedApiRequest
      // doesn't conflict with the new-chat state.
      isGeneratingRef.current = false;
      setIsGenerating(false);
      setShowLoadingMessage(false);
      setStreamingStatus(null);
    }

    lastQuestionRef.current = "";
    setActiveCitation(undefined);
    setAnswers([]);
    setConversationId(uuidv4());
    setSelectedConvId("");
    setCurrentConversation(undefined);
    setConversationTitle("");
    setConversationRating(undefined);
    setConversationFeedbackComment("");
    setConversationAttachments([]);
    setUploadError(null);
    setUploadWarning(null);
    setHistoryMessageCount(undefined);
    sessionStartTimeRef.current = null;
    // Clear persisted resume point for the conversation that was just cleared
    if (selectedConvId) {
      clearResumePoint(selectedConvId, tabIdRef.current);
    }
  };

  const stopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setShowLoadingMessage(false);
    setStreamingStatus(null);
    setIsGenerating(false);
  };

  useEffect(() => {
    pruneExpiredResumePoints();
    scrollToBottom("smooth");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onShowCitation = (citation: Citation) => {
    if (window !== null && citation.url != null) {      
      window.open(citation.url!, "_blank")?.focus();
    }
  };

  const _onToggleSettingsPanel = () => {
       
    if (!isSettingsPanelOpen) {
      setIsCitationPanelOpen(false);
    }

    setIsSettingsPanelOpen((prevState) => !prevState);
  };

  const _onClearAllChatHistory = async () => {
    setClearing(true);
    const response = await historyDeleteAll();
    if (!response.ok) {
      setClearingError(true);
    } else {
      setActiveCitation(undefined);
      setChatHistory([]);
      toggleClearAllDialog();      
      setAnswers([]);
      setSelectedConvId("");
      setCurrentConversation(undefined);
      setConversationTitle("");
    }
    setClearing(false);
  };

  const _onHideClearAllDialog = () => {
    toggleClearAllDialog();
    setTimeout(() => {
      setClearingError(false);
    }, 2000);
  };

  const handleSpeech = (index: number, status: string) => {
    if (status != "pause") setActiveCardIndex(index);
    setIsTextToSpeechActive(status == "speak" ? true : false);
  };
 
  const getMessagesByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.messages || [];
    }
    return [];
  };

  const setMessagesByConvId = (id: string, messagesList: ChatMessage[]) => {
    setChatHistory((prev) => prev.map((conv) =>
      String(conv.id) === String(id) ? { ...conv, messages: messagesList } : conv
    ));
  };


  const associateAttachmentsWithMessages = (
    messages: ChatMessage[],
    attachments: ConversationAttachment[]
  ): ChatMessage[] => {
    if (attachments.length === 0) return messages;

    const sorted = [...attachments].sort(
      (a, b) => new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime()
    );

    const userMsgs = messages
      .map((msg, idx) => ({ msg, idx }))
      .filter(({ msg }) => msg.role === "user")
      .sort((a, b) => new Date(a.msg.date).getTime() - new Date(b.msg.date).getTime());

    if (userMsgs.length === 0) return messages;

    const lastUserMsgIdx = userMsgs[userMsgs.length - 1].idx;
    const buckets = new Map<number, ConversationAttachment[]>();

    for (const att of sorted) {
      const attTime = new Date(att.uploaded_at).getTime();
      // First user message sent on or after the upload — this is the message that used this attachment
      const target = userMsgs.find(({ msg }) => new Date(msg.date).getTime() >= attTime);
      const targetIdx = target ? target.idx : lastUserMsgIdx;
      if (!buckets.has(targetIdx)) buckets.set(targetIdx, []);
      buckets.get(targetIdx)!.push(att);
    }

    return messages.map((msg, index) => {
      const atts = buckets.get(index);
      return atts && atts.length > 0 ? { ...msg, attachments: atts } : msg;
    });
  };

  const onSelectConversation = async (id: string) => {
    if (isGenerating) {
      // If response is being generated, prevent switching threads
      return;
    }
    if (!id) {
      console.error("No conversation Id found");
      return;
    }

    // Hide the chat stream content while we load + scroll so the user
    // doesn't see a flash at the wrong scroll position.
    // IMPORTANT: only set scrollPending when the stream div is already in the
    // DOM (i.e. we are currently viewing a conversation, not the home page).
    // When on the home page chatStreamRef is null; setting scrollPending here
    // causes the reveal useEffect to exit early (el = null), the timer is
    // never set, and the chat stream is left permanently invisible once
    // messages load and the div mounts.
    if (chatStreamRef.current) {
      chatStreamRef.current.scrollTop = 0;
      scrollPendingRef.current = true;
      setScrollPending(true);
    }

    const cachedMessages = getMessagesByConvId(id);
    const needsFetch = cachedMessages.length === 0;

    // Fetch attachments and messages in parallel; skip message fetch if cached
    let loadedAttachments: ConversationAttachment[] = [];
    let responseMessages: ChatMessage[] = cachedMessages;
    if (needsFetch) {
      setFetchingConvMessages(true);
    }
    try {
      const [attachments, fetchedMessages] = await Promise.all([
        getConversationAttachments(id).catch(() => [] as ConversationAttachment[]),
        needsFetch ? historyReadMessages(id) : Promise.resolve(cachedMessages),
      ]);
      loadedAttachments = attachments;
      responseMessages = fetchedMessages;
    } catch {
      loadedAttachments = [];
    }

    if (needsFetch) {
      const cleaned = stripTemplateContent(responseMessages, templates);
      const withAttachments = associateAttachmentsWithMessages(cleaned, loadedAttachments);
      setAnswers(withAttachments);
      setMessagesByConvId(id, withAttachments);
      setFetchingConvMessages(false);
      // Check sessionStorage first so a refresh restores the previous divider position.
      // Uses per-tab key so two tabs never overwrite each other; validates and bounds-checks.
      const stored = getResumePoint(id, tabIdRef.current);
      if (stored && stored.resumedAtIndex <= withAttachments.length) {
        setHistoryMessageCount(stored.resumedAtIndex);
        sessionStartTimeRef.current = new Date(stored.sessionStartTime);
      } else {
        const now = new Date();
        setResumePoint(id, tabIdRef.current, { resumedAtIndex: withAttachments.length, sessionStartTime: now.toISOString() });
        setHistoryMessageCount(withAttachments.length);
        sessionStartTimeRef.current = now;
      }
    } else {
      const withAttachments = associateAttachmentsWithMessages(
        stripTemplateContent(responseMessages, templates),
        loadedAttachments
      );
      setAnswers(withAttachments);
      const stored = getResumePoint(id, tabIdRef.current);
      if (stored && stored.resumedAtIndex <= responseMessages.length) {
        setHistoryMessageCount(stored.resumedAtIndex);
        sessionStartTimeRef.current = new Date(stored.sessionStartTime);
      } else {
        const now = new Date();
        setResumePoint(id, tabIdRef.current, { resumedAtIndex: responseMessages.length, sessionStartTime: now.toISOString() });
        setHistoryMessageCount(responseMessages.length);
        sessionStartTimeRef.current = now;
      }
    }

    const conversation = getConversationFromHistory(id);
    setCurrentConversation(conversation);

    setConversationTitle(conversation?.title ?? "");
    setSelectedConvId(id);
    setConversationRating(conversation?.rating ?? undefined)
    setConversationFeedbackComment(conversation?.additional_comments ?? "")
    setShowStartNewChatBtn(true);

    // Reuse already-fetched attachments (no duplicate API call)
    setConversationAttachments(loadedAttachments);
  };

  const getConversationFromHistory = (conversationId: string): Conversation | undefined => {
    return chatHistory.find((obj) => String(obj.id) === conversationId);    
  }

  // Scroll the chat stream container to the very bottom.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "instant") => {
    const el = chatStreamRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // When true the chat stream uses visibility:hidden (invisible but still
  // participates in layout so scrollHeight is accurate).  Flipped back to
  // false after we've scrolled to the bottom.
  const [scrollPending, setScrollPending] = useState(false);
  const scrollPendingRef = useRef(false);

  // After selecting a conversation: observe the chat container for size
  // changes.  Once the container's scrollHeight exceeds viewport height
  // (or after a safety timeout), snap to the bottom and reveal.
  useEffect(() => {
    if (!scrollPendingRef.current) return;
    const el = chatStreamRef.current;
    if (!el) {
      // Stream div not in DOM yet (navigating from home page).
      // Reset immediately so it doesn't get stuck permanently hidden.
      scrollPendingRef.current = false;
      setScrollPending(false);
      return;
    }

    const reveal = () => {
      el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
      scrollPendingRef.current = false;
      setScrollPending(false);
    };

    // Use ResizeObserver to detect when content has been rendered
    // (the container's height or scrollHeight changes as React renders messages).
    const ro = new ResizeObserver(() => {
      // Snap to bottom on every resize while pending.
      el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
    });
    ro.observe(el);

    // Safety timeout: reveal after 600ms regardless, so the UI is never stuck hidden.
    const timer = setTimeout(() => {
      ro.disconnect();
      reveal();
    }, 600);

    return () => {
      ro.disconnect();
      clearTimeout(timer);
    };
  }, [scrollPending]);

  const _onHistoryTitleChange = (id: string, newTitle: string) => {
    const tempChatHistory = [...chatHistory];
    const index = tempChatHistory.findIndex((obj) => obj.id === id);
    if (index > -1) {
      tempChatHistory[index].title = newTitle;
      setChatHistory(tempChatHistory);
      if (selectedConvId === tempChatHistory[index].id) {
        setConversationTitle(newTitle);
      }
    }
  };

  useEffect(() => {
    setIsInitialAPItriggered(true);
    setShowStartupScreen(getShowedStartupScreen() == false);
  }, []);

  useEffect(() => {
    if (isInitialAPItriggered && !showStartupScreen) {
      // ShellContext already loads initial history on mount;
      // only fetch here for paginated "load more" triggered by scroll.
      // Fire settings check without blocking — history is already loaded.
      getFrontEndSettings().catch(() => {});
    }
  }, [isInitialAPItriggered, showStartupScreen]);

  // Open a specific conversation when the sidebar ?chat=<id> URL param is set
  const chatIdParam = searchParams.get("chat");
  useEffect(() => {
    if (chatIdParam && chatIdParam !== selectedConvId && !isGenerating) {
      onSelectConversation(chatIdParam);
    }
  }, [chatIdParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab sync: when another tab sends a message in the same conversation,
  // invalidate this tab's cached resume point so the divider reflects a fresh
  // position if the user re-selects that conversation.
  useEffect(() => {
    const channel = new BroadcastChannel("chat_resume_sync");
    broadcastRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      const { type, convId } = event.data as { type: string; convId: string };
      if (type === "CONVERSATION_UPDATED" && convId === selectedConvId) {
        clearResumePoint(convId, tabIdRef.current);
      }
    };
    return () => {
      channel.close();
      broadcastRef.current = null;
    };
  }, [selectedConvId]);

  const _onHistoryDelete = (id: string) => {
    const tempChatHistory = [...chatHistory];
    tempChatHistory.splice(
      tempChatHistory.findIndex((a) => a.id === id),
      1
    );
    setChatHistory(tempChatHistory);
    if (id === selectedConvId) {
      lastQuestionRef.current = "";
      setActiveCitation(undefined);
      setAnswers([]);
      setSelectedConvId("");
      setCurrentConversation(undefined);
      setConversationTitle("");
    }
  };

  const _handleFetchHistory = async () => {
    if (fetchingChatHistory || !hasMoreRecords) {
      return;
    }
    setFetchingChatHistory(true);
    await historyList(offset).then((response) => {
      if (Array.isArray(response)) {
        setChatHistory((prevData) => [...prevData, ...response]);
        if (response.length === OFFSET_INCREMENT) {
          setOffset((offset) => (offset += OFFSET_INCREMENT));
          // Stopping offset increment if there were no records
        } else if (response.length < OFFSET_INCREMENT) {
          setHasMoreRecords(false);
        }
      } else {
        setChatHistory([]);
      }
      setFetchingChatHistory(false);
      return response;
    });
  };

  useEffect(() => {
    if (!isGenerating) return;
    const frame = requestAnimationFrame(() => {
      if (loadingTopRef.current && chatStreamRef.current) {
        chatStreamRef.current.scrollTo({
          top: loadingTopRef.current.offsetTop,
          behavior: 'smooth',
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isGenerating]);

  const loadingMessageBlock = () => {
    return (
      <React.Fragment key="generating-answer">
        {!isRetryRef.current && (
        <div className={styles.chatMessageUser}>
          <div className={styles.chatMessageUserMessage}>
            {lastQuestionRef.current}
          </div>
        </div>
        )}
        <div className={styles.chatMessageGpt} data-testid="generatingAnswer">
          {streamingStatus !== null ? (
            <div className={styles.statusBubble}>
              {streamingStatus.replace(/\.{3}$/, '')}
            </div>
          ) : (
            <Answer
              answer={{
                conversationId: "",
                messageId: "",
                answer: "Generating answer...",
                citations: [],
              }}
              onCitationClicked={() => null}
              index={0}
              canGiveFeedback={false}
              canCopy={false}
            />
          )}
        </div>
      </React.Fragment>
    );
  };
  const showAssistantTypeSection =
    !fetchingConvMessages && !lastQuestionRef.current && answers.length === 0;
  const showCitationPanel =
    answers.length > 0 && isCitationPanelOpen && activeCitation;

  const showConversationFeedbackDialog = () => {
      setShowConverationFeedbackPopup(true);
  };

  // Sync conversation ID and rate callback to shell context for sidebar access
  useEffect(() => {
    setCurrentConversationId(selectedConvId || null);
  }, [selectedConvId, setCurrentConversationId]);

  useEffect(() => {
    setOnRateConversation(() => showConversationFeedbackDialog);
    return () => setOnRateConversation(null);
  }, [setOnRateConversation]);

  // clearChatRef: always points to the latest clearChat function.
  // The useEffect below registers onNewChat only once (setOnNewChat is stable),
  // so without this ref the sidebar would call a stale closure with initial state.
  const clearChatRef = useRef<() => void>(clearChat);
  clearChatRef.current = clearChat;
  useEffect(() => {
    setOnNewChat(() => () => clearChatRef.current());
    return () => setOnNewChat(null);
  }, [setOnNewChat]);

  // When a pending rate matches the loaded conversation, open the feedback dialog
  useEffect(() => {
    if (pendingRateConvId && pendingRateConvId === selectedConvId) {
      setPendingRateConvId(null);
      showConversationFeedbackDialog();
    }
  }, [pendingRateConvId, selectedConvId, setPendingRateConvId]);

  const hideConversationFeedbackDialog = () => {
      setShowConverationFeedbackPopup(false);
  };

  const onSendConversationFeedback = async () => {
    if (!conversationRating || !selectedConvId) {
      return;
    }
    
    const conversation = getConversationFromHistory(selectedConvId);
    if (!conversation) {
      console.error("conversation " + selectedConvId + " not found!");
      return;
    }

    conversation.rating = conversationRating;
    conversation.additional_comments = conversationFeedbackComment;

    try {
      await historyGiveConversationFeedback(
        selectedConvId, 
        conversationRating, 
        conversationFeedbackComment || undefined
      );
      hideConversationFeedbackDialog();
      // Optionally show success message
    } catch (error) {
      console.error("Failed to submit conversation feedback:", error);
      // Handle error
    }
  };

  /**
   * Handle template selection from Cards component
   * Inserts the template command into the input
   */
  const _handleTemplateSelect = (promptKey: string) => {
    // Set the external input value to populate the QuestionInput field
    // This will trigger the slash command hint display
    setExternalInputValue(`/${promptKey} `);
  };

  return (
    <Layout
      onAvailableModelsChanged={setAvailableModels}
      onAvailableIndexesChanged={setAvailableIndexes}
    >
      <div
        className={styles.container}
        onDragEnter={handleChatDragEnter}
        onDragOver={handleChatDragOver}
        onDragLeave={handleChatDragLeave}
        onDrop={handleChatDrop}
      >
        {isDragOver && createPortal(
          <div
            className={styles.dropOverlay}
            onDragEnter={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); dragCounterRef.current += 1; } }}
            onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
            onDragLeave={(e) => { e.preventDefault(); dragCounterRef.current -= 1; if (dragCounterRef.current === 0) setIsDragOver(false); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current = 0; setIsDragOver(false); const files = e.dataTransfer.files; if (files && files.length > 0) handleFileSelected(files); }}
          >
            <div className={styles.dropTarget}>
              <div className={styles.dropTargetIconWrapper}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 3L12 15M12 3L8.5 6.5M12 3L15.5 6.5" stroke="#004A88" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17" stroke="#004A88" strokeWidth="1.75" strokeLinecap="round"/>
                </svg>
              </div>
              <div className={styles.dropTargetTitle}>Drop to attach</div>
              <div className={styles.dropTargetSubtitle}>PDF · DOCX · TXT · MD · CSV · JSON · XML · LOG<br />PNG · JPG · WEBP · SVG &nbsp;·&nbsp; max 20 MB per file</div>
            </div>
          </div>,
          document.body
        )}
        <Stack horizontal className={styles.chatRoot}>
          <div
            className={`${styles.chatContainer} ${styles.MobileChatContainer}`}
          >
            {showStartupScreen ? (
              <StartupScreen 
                  isAssistantAPILoading={isAssistantAPILoading}
                  onStartupComplete={handleStartupComplete}
                />
            ) : (
              <>
                {showAssistantTypeSection ? (
                  <>
                  <div className={styles.homeWrapper}>
                    <AssistantTypeSection
                      isAssistantAPILoading={isAssistantAPILoading}
                      onExampleClicked={(content) => makeStreamedApiRequest(content, false)}
                      availableIndexes={availableIndexes}
                    />
                    {!isAssistantAPILoading && (
                      <div className={styles.chatInputContainerHome}>
                        <Stack horizontal className={styles.chatInput}>
                          <QuestionInput
                            clearOnSend
                            placeholder="Ask me anything about Diebold Nixdorf products & services..."
                            disabled={isGenerating}
                            onSend={(question, isProposedAnswer) => makeStreamedApiRequest(question, isProposedAnswer)}
                            onStopGenerating={stopGenerating}
                            recognizedText={recognizedText}
                            isSendButtonDisabled={isSendButtonDisabled}
                            onMicrophoneClick={onMicrophoneClick}
                            onStopClick={stopSpeechRecognition}
                            isListening={isListening}
                            isRecognizing={isRecognizing}
                            setRecognizedText={setRecognizedText}
                            isTextToSpeechActive={isTextToSpeechActive}
                            proposedAnswers={proposedAnswers}
                            canRequestResolutionCode={canRequestResolutionCode}
                            onRequestResolutionCode={onRequestResolutionCode}
                            externalInputValue={externalInputValue}
                            onFileSelected={handleFileSelected}
                            attachments={conversationAttachments}
                            onDeleteAttachment={handleDeleteAttachment}
                            isUploading={uploadingFiles}
                          />
                        </Stack>
                      </div>
                    )}
                  </div>
                  <div className={styles.documentUploadTip}>
                    💬 Your expertise matters! Share important documents or feedback with us at{" "}
                    <a href="mailto:RetailAICoreTeam@dieboldnixdorf.com" className={styles.documentUploadTipLink}>
                      RetailAICoreTeam@dieboldnixdorf.com
                    </a>{" "}
                    to help enhance our knowledge base.
                  </div>
                  </>
                ) : (
                  <>
                    {/* Chat header bar — shown only when messages are visible */}
                    <div className={styles.chatHeaderBar}>
                      <span className={styles.chatHeaderTitle}>
                        {conversationTitle || "Chat"}
                      </span>
                      <button
                        className={`${styles.chatHeaderActionBtn} ${findOpen ? styles.chatHeaderActionBtnActive : ""}`}
                        onClick={() => (findOpen ? handleFindClose() : setFindOpen(true))}
                        title="Find in chat (Ctrl+F)"
                        aria-label="Find in chat"
                        aria-pressed={findOpen}
                      >
                        <Search20Regular />
                      </button>
                    </div>

                    {/* Find bar — slides in below the header */}
                    {findOpen && (
                      <ChatFindBar
                        query={findQuery}
                        onQueryChange={(q) => { setFindQuery(q); setFindCurrentIndex(0); }}
                        currentIndex={findCurrentIndex}
                        total={findTotal}
                        onNext={handleFindNext}
                        onPrev={handleFindPrev}
                        onClose={handleFindClose}
                      />
                    )}

                    <div
                      className={styles.chatMessageStream}
                      style={scrollPending ? { visibility: "hidden" } : undefined}
                      ref={chatStreamRef}
                    >
                      <ChatMessageContainer
                        conversationId={selectedConvId || conversationId}
                        activeCardIndex={activeCardIndex}
                        answers={answers}
                        fetchingConvMessages={fetchingConvMessages || (templatesLoading && answers.length > 0 && !isGenerating)}
                        handleSpeech={handleSpeech}
                        onShowCitation={onShowCitation}
                        onFeedbackUpdate={handleFeedbackUpdate}
                        resumedAtIndex={historyMessageCount}
                        sessionStartTime={sessionStartTimeRef.current ?? undefined}
                        findQuery={findOpen ? findQuery : undefined}
                        onSuggestedQuestionClicked={(question) => makeStreamedApiRequest(question, true)}
                        onEditMessage={(_index, newContent) => makeStreamedApiRequest(newContent, false)}
                        isGenerating={isGenerating}
                      />
                      {isGenerating && <div ref={loadingTopRef} />}
                      {(showLoadingMessage || streamingStatus !== null) && loadingMessageBlock()}
                      {canRetry && (
                        <div className={styles.retryBar}>
                          <span className={styles.retryLabel}>The response didn't complete. Would you like to try again?</span>
                          <button className={styles.retryBtn} onClick={handleRetry}>
                            Retry
                          </button>
                        </div>
                      )}
                      <div data-testid="streamendref-id" ref={chatMessageStreamEnd} />
                      {isGenerating && <div className={styles.generatingSpacer} />}
                    </div>
                  </>
                )}

                {!isAssistantAPILoading && !showAssistantTypeSection && (
                  <div className={`${styles.chatInputContainer} ${styles.chatInputContainerFull}`}>
                    <div>
                      {isRecognizing && !isListening && <p>Please wait...</p>}{" "}
                      {isListening && <p>Listening...</p>}{" "}
                    </div>

                    <Stack horizontal className={styles.chatInput}>
                      <QuestionInput
                        clearOnSend
                        placeholder="Ask me anything about Diebold Nixdorf products & services..."
                        disabled={isGenerating}
                        onSend={(question, isProposedAnswer) => makeStreamedApiRequest(question, isProposedAnswer)}
                        onStopGenerating={stopGenerating}
                        recognizedText={recognizedText}
                        isSendButtonDisabled={isSendButtonDisabled}
                        onMicrophoneClick={onMicrophoneClick}
                        onStopClick={stopSpeechRecognition}
                        isListening={isListening}
                        isRecognizing={isRecognizing}
                        setRecognizedText={setRecognizedText}
                        isTextToSpeechActive={isTextToSpeechActive}
                        proposedAnswers={proposedAnswers}
                        canRequestResolutionCode={canRequestResolutionCode}
                        onRequestResolutionCode={onRequestResolutionCode}
                        externalInputValue={externalInputValue}
                        onFileSelected={handleFileSelected}
                        attachments={conversationAttachments}
                        onDeleteAttachment={handleDeleteAttachment}
                        isUploading={uploadingFiles}
                        uploadError={uploadError}
                        onDismissUploadError={() => setUploadError(null)}
                        uploadWarning={uploadWarning}
                        onDismissUploadWarning={() => setUploadWarning(null)}
                      />
                    </Stack>
                  </div>
                )}
              </>
            )}
          </div>
          {showCitationPanel && (
            <CitationPanel
              activeCitation={activeCitation}
              setIsCitationPanelOpen={setIsCitationPanelOpen}
            />
          )}

          {isSettingsPanelOpen && (
            <SettingsPanel
              availableModels={availableModels}
              availableIndexes={availableIndexes}
              setIsSettingsPanelOpen={setIsSettingsPanelOpen}             
            />
          )}
        </Stack>

        {showConversationFeedbackPopup && createPortal(
          <div className={styles.feedbackOverlay} onClick={hideConversationFeedbackDialog}>
            <div className={styles.feedbackPanel} onClick={(e) => e.stopPropagation()}>
              <div className={styles.feedbackHeader}>
                <span className={styles.feedbackTitle}>Rate this conversation</span>
                <button
                  className={styles.feedbackDismiss}
                  onClick={hideConversationFeedbackDialog}
                  aria-label="Close"
                >
                  <DismissRegular />
                </button>
              </div>
              <Stack tokens={{ childrenGap: 16 }}>
                <Text variant="medium">How would you rate this conversation?</Text>
                
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                  <IconButton
                    iconProps={{ iconName: conversationRating === "helpful" ? "LikeSolid" : "Like" }}
                    title="This conversation was helpful"
                    onClick={() => setConversationRating("helpful")}                  
                  />
                  <IconButton
                    iconProps={{ iconName: conversationRating === "not_helpful" ? "DislikeSolid" : "Dislike" }}
                    title="This conversation was not helpful"
                    onClick={() => setConversationRating("not_helpful")}                  
                  />
                </Stack>
                
                <TextField
                  label="Additional comments (optional)"
                  multiline
                  rows={8}
                  value={conversationFeedbackComment}
                  onChange={(_, newValue) => setConversationFeedbackComment(newValue || "")}
                  placeholder="Tell us more about your experience..."
                />
              </Stack>
              <div className={styles.feedbackFooter}>
                <PrimaryButton
                  onClick={onSendConversationFeedback}
                  disabled={!conversationRating}
                  text="Send"
                />
                <DefaultButton
                  onClick={hideConversationFeedbackDialog}                
                  text="Cancel"
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        <ExportModal
          isOpen={isExportModalOpen}
          onDismiss={() => setIsExportModalOpen(false)}
          conversationId={selectedConvId || conversationId}
        />
      </div>
    </Layout>
  );
};

export default Chat;
